<template>
  <div class="pa-0 secondary--text text--darken-3">
    <div class="bg-grey-light pa-8 border-bottom">
      <div class="d-flex align-center mb-2">
        <v-avatar color="primary" size="40" class="mr-4 shadow-sm">
          <v-icon color="white" small>fas fa-credit-card</v-icon>
        </v-avatar>
        <div>
          <div class="text-h5 font-weight-black primary--text lh-1">
            {{ id > -1 ? 'Editar Condición de Pago' : 'Nueva Condición de Pago' }}
          </div>
          <div class="caption grey--text font-weight-bold text-uppercase tracking-wider mt-1">
            Opciones disponibles para las cotizaciones
          </div>
        </div>
      </div>
    </div>

    <div class="pa-8 bg-white">
      <ValidationObserver v-slot="{ handleSubmit }">
        <v-form @submit.prevent="handleSubmit(save)">
          <v-row dense class="mb-2">
            <v-col cols="12">
              <ValidationProvider name="Nombre" rules="required|max:200" v-slot="{ errors }">
                <v-text-field
                  v-model="item.name"
                  label="Nombre (se imprime en el PDF)"
                  placeholder="Ej: 30 días, 50% anticipo / 50% contra entrega"
                  outlined
                  dense
                  class="modern-input bg-white"
                  :error-messages="errors"
                  :loading="loading"
                  prepend-inner-icon="fas fa-tag"
                  counter="200"
                />
              </ValidationProvider>
            </v-col>
            <v-col cols="12" md="6">
              <ValidationProvider name="Días" rules="minvalue:0" v-slot="{ errors }">
                <v-text-field
                  v-model="item.days"
                  label="Días de plazo (opcional)"
                  type="number"
                  min="0"
                  outlined
                  dense
                  class="modern-input bg-white"
                  :error-messages="errors"
                  hint="Se usa para preseleccionar según los días de pago del cliente"
                  persistent-hint
                  prepend-inner-icon="fas fa-calendar-day"
                />
              </ValidationProvider>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="item.sort"
                label="Orden en la lista"
                type="number"
                outlined
                dense
                class="modern-input bg-white"
                prepend-inner-icon="fas fa-sort-numeric-down"
              />
            </v-col>
            <v-col cols="12">
              <v-switch
                v-model="item.active"
                inset
                :color="item.active ? 'success' : 'grey'"
                :label="item.active ? 'Activa: disponible en nuevas cotizaciones' : 'Inactiva: no aparece en nuevas cotizaciones'"
                hide-details
              />
            </v-col>
          </v-row>

          <div class="d-flex mt-10 gap-3 align-center">
            <v-btn
              v-if="id > -1"
              icon
              color="error"
              class="grey lighten-4 rounded-lg mr-2"
              large
              @click="showConfirmDelete = true"
              :disabled="loading"
              title="Eliminar registro"
            >
              <v-icon small>fas fa-trash-alt</v-icon>
            </v-btn>

            <v-btn
              type="submit"
              color="primary"
              large
              depressed
              :loading="loading"
              class="flex-grow-1 py-6 rounded-lg font-weight-black text-uppercase tracking-widest shadow-md"
            >
              <v-icon small class="mr-2">fas fa-save</v-icon>
              {{ id > -1 ? 'Actualizar Datos' : 'Registrar Condición' }}
            </v-btn>

            <v-btn
              text
              large
              color="grey darken-1"
              class="rounded-lg font-weight-bold px-6"
              @click.prevent="$emit('close')"
            >
              Cancelar
            </v-btn>
          </div>
        </v-form>
      </ValidationObserver>
    </div>

    <confirm
      v-model="showConfirmDelete"
      :message="`¿Confirma eliminar la condición de pago '${item.name}'? Si ya se usó en cotizaciones, deberá desactivarla en lugar de eliminarla.`"
      @ok="deleteRecord"
      @cancel="showConfirmDelete = false"
    />
  </div>
</template>

<script>
import { getPaymentCondition, createPaymentCondition, updatePaymentCondition, deletePaymentCondition } from '@/api/salesNotes'
import notifications from '@/utils/notifications'

const emptyItem = () => ({ id: -1, name: '', days: '', sort: 0, active: true })

export default {
  name: 'PaymentConditionCreateUpdate',
  props: {
    id: { required: true }
  },
  data() {
    return {
      item: emptyItem(),
      loading: false,
      showConfirmDelete: false,
    }
  },
  methods: {
    async save() {
      this.loading = true
      try {
        const payload = {
          name: this.item.name,
          days: this.item.days === '' || this.item.days === null ? null : parseInt(this.item.days),
          sort: parseInt(this.item.sort) || 0,
          active: this.item.active,
        }
        if (this.id > -1) {
          await updatePaymentCondition(this.id, payload)
          notifications.showSuccess('Condición de pago actualizada correctamente')
        } else {
          await createPaymentCondition(payload)
          notifications.showSuccess('Condición de pago registrada correctamente')
        }
        this.$emit('updated')
        this.$emit('close')
      } catch (err) {
        notifications.showError('No se pudo guardar: ' + err)
      } finally {
        this.loading = false
      }
    },
    async get() {
      if (this.id > -1) {
        this.loading = true
        try {
          const res = await getPaymentCondition(this.id)
          this.item = { ...res, days: res.days === null ? '' : res.days }
        } catch (err) {
          notifications.showError('No se pudieron cargar los datos')
        } finally {
          this.loading = false
        }
      } else {
        this.item = emptyItem()
      }
    },
    async deleteRecord() {
      this.loading = true
      try {
        await deletePaymentCondition(this.id)
        notifications.showSuccess('Condición de pago eliminada correctamente')
        this.$emit('updated')
        this.$emit('close')
      } catch (err) {
        notifications.showError(String(err))
      } finally {
        this.loading = false
        this.showConfirmDelete = false
      }
    },
  },
  watch: {
    id: { handler: 'get', immediate: true }
  }
}
</script>

<style scoped>
.bg-grey-light { background-color: #f8fafc; }
.bg-white { background-color: white !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.06); }
.lh-1 { line-height: 1 !important; }
.tracking-widest { letter-spacing: 2px; }
.tracking-wider { letter-spacing: 1px; }
.gap-3 { gap: 12px; }

.modern-input >>> .v-input__control { min-height: 48px !important; }
.modern-input >>> fieldset { border: 1px solid rgba(0,0,0,0.08) !important; border-radius: 8px !important; transition: all 0.3s ease; }
.modern-input >>> input { font-size: 0.95rem !important; font-weight: 500; }

.shadow-md { box-shadow: 0 4px 12px rgba(var(--v-primary-base), 0.2) !important; }
.shadow-sm { box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important; }
</style>
